import { Injectable } from '@angular/core';
import EmployeeWithBudgets from '../models/EmployeeWithBudgets';
import Budget from '../models/Budget';

/**
 * Replicates budgets-list monthly total and hourly budgeted cost logic exactly,
 * so other components can compute rates identical to the Budgets Overview.
 */
@Injectable({ providedIn: 'root' })
export class BudgetParityService {
  private readonly WORKING_DAYS_PER_MONTH = 17;
  private readonly HOURS_PER_DAY = 8;
  private readonly STANDARD_WORKING_HOURS = this.WORKING_DAYS_PER_MONTH * this.HOURS_PER_DAY; // 136

  /** Copy of budgets-list simpleProrate */
  simpleProrate(totalSpend: number, startDateStr: string, endDateStr: string): number {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    end.setDate(end.getDate() - 1);
    if (end < start) return 0;

    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    const baseMonths = yearDiff * 12 + monthDiff;
    const extra = end.getDate() >= start.getDate() ? 1 : 0;
    const months = baseMonths + extra;
    if (months <= 0) return 0;

    return Math.round((totalSpend / months + Number.EPSILON) * 100) / 100;
  }

  /** Range aligned to budgets-list (strict isAfter/isBefore) */
  private isWithinRange(selectedDate: Date, startStr: string, endStr: string): boolean {
    const selTs = selectedDate.getTime();
    const startTs = new Date(startStr).getTime();
    const endTs = new Date(endStr).getTime();
    return selTs > startTs && selTs < endTs;
  }

  /** Filter active budgets for target date and not archived */
  private getActiveBudgets(budgets: Budget[], selectedDate: Date): Budget[] {
    const unarchived = (budgets || []).filter(b => !b.archived);
    return unarchived.filter(b => this.isWithinRange(selectedDate, b.startDate, b.endDate));
  }

  private getBudgetsByType(budgetType: string | null, budgets: Budget[]): Budget[] {
    if (budgetType) {
      return budgets.filter((b: any) => b.repeat === budgetType && b.recurring === true);
    }
    return budgets.filter((b: any) => b.repeat == null || b.recurring === false);
  }

  /** Exact monthly total as in budgets-list.getBudgetedCostUsingList */
  getMonthlyBudgetTotalForEmployee(employeeId: string, selectedDate: Date, employeesWithBudgets: EmployeeWithBudgets[]): number {
    const employee = (employeesWithBudgets || []).find(e => e.uuid === employeeId);
    const budgets = employee?.budgets || [];
    const active = this.getActiveBudgets(budgets, selectedDate);
    if (!active.length) return 0;

    const monthly = this.getBudgetsByType('MONTHLY', active);
    const yearly = this.getBudgetsByType('YEARLY', active);
    const daily = this.getBudgetsByType('DAILY', active);
    const nonRecurring = this.getBudgetsByType(null, active);

    const nonRecurringMonthly = nonRecurring.map(b => ({ ...b, totalSpend: this.simpleProrate(b.totalSpend, b.startDate, b.endDate) }));
    const yearlyMonthly = yearly.map(b => ({ ...b, totalSpend: (b.totalSpend || 0) / 12 }));
    const dailyMonthly = daily.map(b => ({ ...b, totalSpend: (b.totalSpend || 0) * this.WORKING_DAYS_PER_MONTH }));

    const all = [
      ...nonRecurringMonthly,
      ...monthly,
      ...yearlyMonthly,
      ...dailyMonthly,
    ];
    return all.reduce((acc, b) => acc + (b.totalSpend || 0), 0);
  }

  /** Exact hourly budgeted cost as in budgets-list.getHourlyBudgetedCostForEmployee */
  getHourlyBudgetedCostForEmployee(employeeId: string, selectedDate: Date, employeesWithBudgets: EmployeeWithBudgets[]): number {
    const employee = (employeesWithBudgets || []).find(e => e.uuid === employeeId);
    const monthlyTotal = this.getMonthlyBudgetTotalForEmployee(employeeId, selectedDate, employeesWithBudgets);
    const workPercentage = this.resolveWorkPercentage(employee);
    const adjustedHours = this.STANDARD_WORKING_HOURS * (workPercentage / 100);
    if (!adjustedHours) return 0;
    const rate = monthlyTotal / adjustedHours;
    // Format to 2 decimals using toFixed to match budgets UI rounding behavior
    return Number(rate.toFixed(2));
  }

  /**
   * Compute hourly cost using BudgetState.budgetedCostsByYear data for exact parity with overview tables.
   * @param employeeId employee UUID
   * @param year target year
   * @param month 0-11 month index
   * @param budgetedCostsFromState array of EmployeeWithCosts from BudgetState.budgetedCostsByYear(year)
   * @param employeesWithBudgets list to obtain workPercentage
   */
  getHourlyCostFromBudgetedState(
    employeeId: string,
    year: number,
    month: number,
    budgetedCostsFromState: { employeeId: string; year: number; costs: number[] }[],
    employeesWithBudgets: EmployeeWithBudgets[],
  ): number | null {
    const rec = (budgetedCostsFromState || []).find(e => e.employeeId === employeeId && e.year === year);
    if (!rec || !Array.isArray(rec.costs)) return null;
    const monthlyTotal = Number(rec.costs[month] ?? 0);
    const employee = (employeesWithBudgets || []).find(e => e.uuid === employeeId);
    const percentage = this.resolveWorkPercentage(employee);
    const adjustedHours = this.STANDARD_WORKING_HOURS * (percentage / 100);
    if (!adjustedHours) return 0;
    const rate = monthlyTotal / adjustedHours;
    // Format to 2 decimals using toFixed to match budgets UI rounding behavior
    return Number(rate.toFixed(2));
  }

  /**
   * Variant that uses configurable days-per-month to mirror budgets overview when it uses SettingsState.
   */
  getHourlyCostFromBudgetedStateWithDays(
    employeeId: string,
    year: number,
    month: number,
    budgetedCostsFromState: { employeeId: string; year: number; costs: number[] }[],
    employeesWithBudgets: EmployeeWithBudgets[],
    daysPerMonth: number,
  ): number | null {
    const rec = (budgetedCostsFromState || []).find(e => e.employeeId === employeeId && e.year === year);
    if (!rec || !Array.isArray(rec.costs)) return null;
    const monthlyTotal = Number(rec.costs[month] ?? 0);
    const employee = (employeesWithBudgets || []).find(e => e.uuid === employeeId);
    const percentage = this.resolveWorkPercentage(employee);
    const hoursPerMonth = (daysPerMonth || this.WORKING_DAYS_PER_MONTH) * this.HOURS_PER_DAY * (percentage / 100);
    if (!hoursPerMonth) return 0;
    const rate = monthlyTotal / hoursPerMonth;
    return Number(rate.toFixed(2));
  }

  private resolveWorkPercentage(employee: EmployeeWithBudgets): number {
    if (employee?.workPercentage != null && employee.workPercentage > 0) {
      return employee.workPercentage;
    }

    const linkedEmployeeWorkPercentage = employee?.budgets
      ?.find((budget) => budget?.linkedEmployee?.workPercentage != null && budget.linkedEmployee.workPercentage > 0)
      ?.linkedEmployee?.workPercentage;

    if (linkedEmployeeWorkPercentage != null && linkedEmployeeWorkPercentage > 0) {
      return linkedEmployeeWorkPercentage;
    }

    return 100;
  }
}
